/** Spatial hash for fast collision detection. */

import type { Vec2 } from './hex';
import type { Bubble } from '../types';

export interface SpatialHash {
  readonly cellSize: number;
  readonly cells: Map<string, Bubble[]>;
}

/** Create a new empty spatial hash with the given cell size. */
export function createSpatialHash(cellSize: number): SpatialHash {
  return { cellSize, cells: new Map() };
}

/** Compute the cell key for a given pixel position. */
function cellKey(hash: SpatialHash, position: Vec2): string {
  const cx = Math.floor(position.x / hash.cellSize);
  const cy = Math.floor(position.y / hash.cellSize);
  return `${cx},${cy}`;
}

/** Insert a bubble into the spatial hash at the given pixel position. */
export function insert(hash: SpatialHash, bubble: Bubble, pixelPos: Vec2): void {
  const key = cellKey(hash, pixelPos);
  const existing = hash.cells.get(key);
  if (existing) {
    existing.push(bubble);
  } else {
    hash.cells.set(key, [bubble]);
  }
}

/** Query the center cell and 8 surrounding cells for all bubbles near a position. */
export function queryNeighborhood(hash: SpatialHash, position: Vec2): Bubble[] {
  const cx = Math.floor(position.x / hash.cellSize);
  const cy = Math.floor(position.y / hash.cellSize);
  const result: Bubble[] = [];

  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      const key = `${cx + dx},${cy + dy}`;
      const cell = hash.cells.get(key);
      if (cell) {
        for (const bubble of cell) {
          result.push(bubble);
        }
      }
    }
  }

  return result;
}

/** Clear all entries from the spatial hash. */
export function clear(hash: SpatialHash): void {
  hash.cells.clear();
}
